/**
 * ui.ts — helper กลางของหน้าจอ Phase 7 (ตาราง ฟอร์ม ตัวกรอง drawer pagination)
 * ทุกข้อความเป็นภาษาไทย; ตารางใหญ่เลื่อนแนวนอนได้เพื่อไม่ให้ข้อมูลการเงินหาย (UX contract)
 */
import { formatTHB } from './format.js';
import { getTenant } from './state.js';

export const esc = (s: unknown): string =>
  String(s ?? '').replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!,
  );

/** เงินจากค่าที่อาจเป็น null/undefined */
export const money = (v: unknown): string => formatTHB(String(v ?? '0'));

/** ห่อหน้าด้วยหัวข้อ; โหมด demo (ไม่มี tenant) แสดงคำแนะนำแทน body */
export function pageShell(title: string, body: string, headExtra = ''): string {
  const inner = getTenant()
    ? body
    : `<section class="card"><p class="muted">หน้านี้ใช้ได้ในโหมดข้อมูลจริง — กรุณาเข้าสู่ระบบด้วยบัญชี Supabase</p></section>`;
  return `<div class="page-head"><h1>${esc(title)}</h1>${headExtra}</div>${inner}`;
}

export interface TableRow {
  cells: string[];
  /** attribute เพิ่มบน <tr> เช่น data-id */
  attrs?: string | undefined;
  /** class ของแถว เช่น danger (SKU ไม่มีต้นทุน) */
  cls?: string | undefined;
}

export function dataTable(
  headers: string[],
  rows: TableRow[],
  empty = 'ยังไม่มีข้อมูล',
): string {
  if (rows.length === 0) return `<p class="muted">${esc(empty)}</p>`;
  const body = rows
    .map(
      (r) =>
        `<tr${r.cls ? ` class="${r.cls}"` : ''}${r.attrs ? ` ${r.attrs}` : ''}>${r.cells
          .map((c) => `<td>${c}</td>`)
          .join('')}</tr>`,
    )
    .join('');
  return `<div class="table-scroll"><table class="data"><thead><tr>${headers
    .map((h) => `<th>${esc(h)}</th>`)
    .join('')}</tr></thead><tbody>${body}</tbody></table></div>`;
}

export const pill = (text: string, kind: 'ok' | 'warn' | 'danger' | 'plain' = 'plain'): string => {
  const cls = kind === 'ok' ? 'pill active' : kind === 'plain' ? 'pill' : `pill ${kind}`;
  return `<span class="${cls}">${esc(text)}</span>`;
};

/** อ่านค่าจากฟอร์ม (trim) */
export const val = (f: HTMLFormElement, name: string): string =>
  (
    f.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null
  )?.value.trim() ?? '';

export const checked = (f: HTMLFormElement, name: string): boolean =>
  (f.elements.namedItem(name) as HTMLInputElement | null)?.checked ?? false;

/** ค่าที่ไม่ว่างเท่านั้น (ไม่ส่ง field ว่างไป API) */
export const opt = (f: HTMLFormElement, name: string): string | undefined => val(f, name) || undefined;

/** ผูก submit ของฟอร์มกับ handler + แสดงสถานะ/ข้อผิดพลาดภาษาไทย */
export function bindSubmit(
  form: HTMLFormElement,
  msg: HTMLElement,
  handler: (f: HTMLFormElement) => Promise<void>,
  onDone?: () => void | Promise<void>,
  opts: { reset?: boolean; quiet?: boolean } = { reset: true },
): void {
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    msg.className = 'muted';
    msg.textContent = 'กำลังบันทึก…';
    handler(form)
      .then(async () => {
        // quiet = handler เขียนข้อความเองแล้ว (เช่น ผลการค้นหา) จึงไม่เขียนทับ
        if (!opts.quiet) {
          msg.className = 'muted';
          msg.textContent = 'บันทึกแล้ว ✓';
        }
        if (opts.reset !== false) form.reset();
        await onDone?.();
      })
      .catch((err: unknown) => {
        msg.className = 'error';
        msg.textContent = err instanceof Error ? err.message : 'บันทึกไม่สำเร็จ';
      });
  });
}

/** แสดง error ในกล่องเนื้อหา (ใช้เวลาโหลดรายการล้มเหลว) */
export const errorBox = (e: unknown): string =>
  `<p class="error">${esc(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')}</p>`;

/** ตัวเลื่อนหน้า (pagination) — คืน HTML + ให้ผู้เรียกผูก event เอง */
export function pagerHtml(total: number, limit: number, offset: number): string {
  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  return `<div class="pager"><button class="mini" data-page="prev"${offset === 0 ? ' disabled' : ''}>‹ ก่อนหน้า</button>
    <span class="muted">หน้า ${page}/${pages} · ทั้งหมด ${total} รายการ</span>
    <button class="mini" data-page="next"${page >= pages ? ' disabled' : ''}>ถัดไป ›</button></div>`;
}

export function bindPager(
  root: ParentNode,
  state: { limit: number; offset: number },
  refresh: () => void | Promise<void>,
): void {
  root.querySelectorAll<HTMLButtonElement>('button[data-page]').forEach((b) =>
    b.addEventListener('click', () => {
      state.offset =
        b.dataset['page'] === 'prev'
          ? Math.max(0, state.offset - state.limit)
          : state.offset + state.limit;
      void refresh();
    }),
  );
}

/** drawer ด้านล่างหน้าจอ (ใช้ดูรายละเอียด/ประวัติ/แก้ไข) */
export function showDrawer(root: ParentNode, title: string, html: string): HTMLElement {
  const drawer = root.querySelector<HTMLElement>('#screen-drawer');
  if (!drawer) throw new Error('ไม่พบ drawer ในหน้านี้');
  drawer.innerHTML = `<div class="drawer-head"><strong>${esc(title)}</strong><button id="drawer-close" class="pill">ปิด</button></div><div class="drawer-body">${html}</div>`;
  drawer.hidden = false;
  drawer.querySelector('#drawer-close')?.addEventListener('click', () => {
    drawer.hidden = true;
  });
  drawer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  return drawer;
}

export const drawerHtml = '<div id="screen-drawer" class="drawer" hidden></div>';

/** แปลงชื่อคอลัมน์ DB → ป้ายภาษาไทยที่อ่านง่าย (ใช้ในประวัติ audit) */
const FIELD_LABELS: Record<string, string> = {
  total_amount: 'ยอดรวม',
  paid_amount: 'จ่ายจริง',
  outstanding_amount: 'ค้างจ่าย',
  amount_before_vat: 'ก่อน VAT',
  vat_amount: 'VAT',
  wht_amount: 'หัก ณ ที่จ่าย',
  event_date: 'วันที่เกิดรายการ',
  accounting_date: 'วันที่ลงบัญชี',
  payment_date: 'วันที่จ่ายจริง',
  document_date: 'วันที่เอกสาร',
  counterparty_name: 'คู่ค้า',
  pnl_category: 'หมวด P&L',
  classification: 'ประเภทรายการ',
  pnl_impact: 'กระทบ P&L',
  review_status: 'สถานะตรวจสอบ',
  reason: 'เหตุผล',
  unit_cost: 'ต้นทุนต่อหน่วย',
  effective_from: 'วันที่เริ่มมีผล',
  effective_to: 'วันที่สิ้นสุด',
};
export const fieldLabel = (col: string): string => FIELD_LABELS[col] ?? col;

/** ป้ายภาษาไทยของ classification */
export const CLASSIFICATION_LABELS: Record<string, string> = {
  income: 'รายได้อื่น',
  expense: 'ค่าใช้จ่าย',
  asset: 'ทรัพย์สิน',
  liability: 'หนี้สิน',
  tax_only: 'ทะเบียนภาษี',
  adjustment: 'รายการปรับปรุง',
  reversal: 'กลับรายการ',
};

/**
 * promptDialog — กล่องกรอกข้อความในแอป (แทน window.prompt ที่บล็อกจอ)
 * คืน string ที่กรอก หรือ null ถ้ายกเลิก/ว่าง
 */
export function promptDialog(opts: {
  title: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  inputMode?: 'text' | 'decimal';
}): Promise<string | null> {
  return new Promise((resolve) => {
    const back = document.createElement('div');
    back.className = 'modal-back';
    back.innerHTML = `<div class="modal" role="dialog" aria-modal="true" aria-label="${esc(opts.title)}">
      <h3 class="modal-title">${esc(opts.title)}</h3>
      ${opts.label ? `<label class="modal-label">${esc(opts.label)}</label>` : ''}
      <input class="modal-input" type="text" inputmode="${opts.inputMode ?? 'text'}"
             value="${esc(opts.defaultValue ?? '')}" placeholder="${esc(opts.placeholder ?? '')}" />
      <div class="modal-actions">
        <button type="button" class="modal-cancel">ยกเลิก</button>
        <button type="button" class="modal-ok">${esc(opts.confirmText ?? 'ยืนยัน')}</button>
      </div>
    </div>`;
    const input = back.querySelector<HTMLInputElement>('.modal-input')!;
    const done = (v: string | null): void => {
      back.remove();
      resolve(v);
    };
    back.querySelector('.modal-cancel')!.addEventListener('click', () => done(null));
    back.querySelector('.modal-ok')!.addEventListener('click', () => done(input.value.trim() || null));
    back.addEventListener('click', (e) => {
      if (e.target === back) done(null);
    });
    input.addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === 'Enter') done(input.value.trim() || null);
      else if (k === 'Escape') done(null);
    });
    document.body.appendChild(back);
    input.focus();
  });
}

/** confirmDialog — กล่องยืนยัน ใช่/ไม่ ในแอป */
export function confirmDialog(opts: {
  title: string;
  message?: string;
  confirmText?: string;
  danger?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    const back = document.createElement('div');
    back.className = 'modal-back';
    back.innerHTML = `<div class="modal" role="dialog" aria-modal="true" aria-label="${esc(opts.title)}">
      <h3 class="modal-title">${esc(opts.title)}</h3>
      ${opts.message ? `<p class="modal-msg">${esc(opts.message)}</p>` : ''}
      <div class="modal-actions">
        <button type="button" class="modal-cancel">ยกเลิก</button>
        <button type="button" class="modal-ok${opts.danger ? ' danger' : ''}">${esc(opts.confirmText ?? 'ยืนยัน')}</button>
      </div>
    </div>`;
    const done = (v: boolean): void => {
      back.remove();
      resolve(v);
    };
    back.querySelector('.modal-cancel')!.addEventListener('click', () => done(false));
    back.querySelector('.modal-ok')!.addEventListener('click', () => done(true));
    back.addEventListener('click', (e) => {
      if (e.target === back) done(false);
    });
    document.body.appendChild(back);
    back.querySelector<HTMLButtonElement>('.modal-ok')!.focus();
  });
}
